import React, { useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useController } from "react-hook-form";
import StyledFormControl from "./StyledFormControl";
import { UploadIcon } from "../Icon";

const isFileAccepted = (file, accept) => {
  if (!accept) return true;

  const mimeTypes = Object.keys(accept);
  const extensions = mimeTypes.flatMap((type) => accept[type]);

  // Check MIME
  if (mimeTypes.includes(file.type)) return true;

  // Check extension fallback
  return extensions.some((ext) =>
    file.name.toLowerCase().endsWith(ext.toLowerCase())
  );
};

const DynamicUploadInput = ({
  name,
  control,
  label = "Upload",
  helperText = "",
  required = false,
  uploadType = "file", // file | folder | both
  multiple = false,
  accept,
}) => {
  const inputRef = useRef(null);

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: required
      ? {
          required:
            typeof required === "string" ? required : "This field is required",
        }
      : undefined,
  });

//   const onDrop = (files) => {
//     const value = multiple
//       ? files.map((file) => ({ progress: 0, file, isComplete: false }))
//       : files[0];
//     field.onChange(value);
//     onUpload?.(value);
//   };

  const onDrop = (acceptedFiles) => {
    let files = acceptedFiles;

    // Manual accept validation for folders
    if (accept) {
      files = acceptedFiles.filter((file) =>
        isFileAccepted(file, accept)
      );

      if (!files.length) {
        field.onChange(null);
        return;
      }
    }

    const value = multiple ? files : files[0];
    field.onChange(value);
  };

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true, // 🚨 IMPORTANT
    multiple,
    accept: uploadType === "file" ? accept : undefined,
  });

  const openPicker = () => inputRef.current?.click();

  return (
    <StyledFormControl
      label={label}
      id={name}
      error={!!error}
      helperText={error?.message || helperText}
      showLabel
    >
      <Box
        {...getRootProps()}
        onClick={openPicker}
        sx={(theme) => ({
          border: `2px dashed ${error ? theme.palette.error.main : "#ccc"}`,
          p: 3,
          textAlign: "center",
          borderRadius: 2,
          cursor: "pointer",
          bgcolor: isDragActive ? "#f5f5f5" : "transparent",
        })}
      >
        {/* MANUAL INPUT (THIS FIXES FOLDER PICKER) */}
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple={multiple}
          accept={uploadType === "file" ? undefined : undefined}
          {...(uploadType !== "file" && {
            webkitdirectory: "true",
            directory: "true",
          })}
          onChange={(e) => onDrop(Array.from(e.target.files))}
        />

        <input type="hidden" {...field} />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <UploadIcon sx={{ fontSize: 16 }} />
          <Typography>
            Drag & Drop or{" "}
            <Typography
              component="span"
              sx={{
                textDecoration: "underline",
                fontWeight: 500,
                textUnderlineOffset: 5,
              }}
            >
              Choose {uploadType}
            </Typography>
          </Typography>
        </Stack>
      </Box>
    </StyledFormControl>
  );
};

export default React.memo(DynamicUploadInput);
